import {Component, computed, EventEmitter, inject, Output, Signal, viewChild} from '@angular/core';
import {CartDocumentDetailComponent} from "./cart-document-detail/cart-document-detail.component";
import {TrapoDetailComponent} from "./trapo-detail/trapo-detail.component";
import {
  ControlService,
  EControlActions,
  IControlObject,
  ICustomColDef,
  TableControlService,
  UploadWrapperComponent,
} from "frontier";
import {MatSnackBar} from "@angular/material/snack-bar";
import {IPdfOverlayToggleEvent} from "../../carts/pdf-overlay-toggle-event.interface";
import {IPdfOverlay} from "../../service/state.interface";
import {ProcessOverviewStore} from "../../service/process-overview.store";

interface IUploadContent {
  name: string;
  dataType: string;
  body: Blob;
}

@Component({
  selector: 'kpi4me-document-detail',
  standalone: true,
  imports: [
    CartDocumentDetailComponent,
    TrapoDetailComponent,
    UploadWrapperComponent,
  ],
  templateUrl: './document-detail.component.html',
  styleUrl: './document-detail.component.scss'
})
export class DocumentDetailComponent {
  private _uploadWrapperRef: Signal<UploadWrapperComponent> = viewChild(UploadWrapperComponent);
  private _cartDocumentDetailRef: Signal<CartDocumentDetailComponent> = viewChild(CartDocumentDetailComponent);
  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  cartDocumentDetailHasNoRows: Signal<boolean> = computed((): boolean => {
    const tableRef: CartDocumentDetailComponent = this._cartDocumentDetailRef();
    return tableRef?.rowCountSignal() === 0;
  });
  pdfOverlay: Signal<IPdfOverlay> = this._processOverviewService.pdfOverlay;
  private _trapoDetailRef: Signal<TrapoDetailComponent> = viewChild(TrapoDetailComponent);
  trapoDetailHasNoRows: Signal<boolean> = computed((): boolean => {
    const tableRef: TrapoDetailComponent = this._trapoDetailRef();
    return tableRef?.rowCountSignal() === 0;
  });
  protected snackBar: MatSnackBar = inject(MatSnackBar);
  protected readonly controlService: ControlService = inject(ControlService);
  protected readonly tableControlService: TableControlService = inject(TableControlService);
  uploadContent: IUploadContent;

  @Output() detailDocumentToggleTriggered: EventEmitter<IPdfOverlayToggleEvent>
    = new EventEmitter<IPdfOverlayToggleEvent>();

  onDocumentUploaded(evt: any): void {
    this.uploadContent = {name: evt.name, dataType: evt.fileFormate, body: new Blob([evt.data])};
    this._cartDocumentDetailRef().callActionFunction(EControlActions.create);
  }

  uploadDocument(evt: any): void {
    let cols: ICustomColDef[] = this._cartDocumentDetailRef().columnDefs as ICustomColDef[];

    let propertyname: string = cols.find((col: ICustomColDef): boolean => {
      return col.apiCol.attributetype === 'TDocument';
    }).apiCol.attribute;

    const row: IControlObject = evt.obj;

    if (propertyname && this.uploadContent && row) {
      this.controlService.controlPostUploadDocument(
        this.uploadContent.name,
        this.uploadContent.dataType,
        row.rowid.toString(),
        row.type,
        row.signature,
        propertyname,
        this.uploadContent.body,
      ).subscribe((res: object): void => {
          this._uploadWrapperRef().onDeleteImage();
          if (res) {
            // Success: Reset uploadContent
            this.uploadContent = null;
          } else {
            this.undoNewLine(row);
          }
        }
      );
    } else {
      this._uploadWrapperRef().onDeleteImage();
      this.undoNewLine(row);
    }
  }

  undoNewLine(row: IControlObject): void {
    if (row) {
      // Delete newly created row, since no document could be appended
      this.tableControlService.tableControlDeleteLine({
        InstanceId: this._cartDocumentDetailRef().apiInstance().instanceid,
        RowObjects: [row],
      })
    }

    this.uploadContent = null;

    this.snackBar.open('Beim Hochladen des Dokuments ist leider ein Fehler aufgetreten', 'ok', {
      verticalPosition: 'top',
      duration: 3000,
    });
  }

  onDocumentRowSelected(): void {
    if (this.pdfOverlay()?.type === 'detailDocument') {
      this._cartDocumentDetailRef().toggleDetailDocumentPreview()
    }
  }

  onTrapoRowSelected(): void {
    if (this.pdfOverlay()?.type === 'detailTrapoDocument') {
      this._trapoDetailRef().toggleDetailTrapoDocumentPreview();
    }
  }
}
